/* eslint-disable no-return-await */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { XMLParser, XMLBuilder } from "fast-xml-parser";

import { json2xml } from "@/helpers/xml";
import requests from "@/services/requests";
import { isSessionExpiredError } from "@/services/sessionExpiredError";
import response from "@/services/response";
import { getSoapIdioma } from "@/utils/soapIdioma";
import { syncLocaleAfterLogin } from "@/utils/appLocale";
import moment from "moment";
import { decodeHTML } from "@/helpers/strings";
import { parseSignedValue } from "@/helpers/orderDuplication";
import { normalizeCurrencyCode, getCurrencySymbol } from "@/helpers/currency";

// ─── Helpers (same logic as Vuex module) ─────────────────────────────────────

function normalizeDatosClienteHtmlEntities(areaVenta) {
  if (!areaVenta?.datosCliente) return;
  const dc = areaVenta.datosCliente;
  if (typeof dc.NOMBRE === "string") dc.NOMBRE = decodeHTML(dc.NOMBRE);
  for (const key of ["SU_COMERCIAL", "SU_GESTOR"]) {
    const o = dc[key];
    if (o && typeof o.NOMBRE === "string") o.NOMBRE = decodeHTML(o.NOMBRE);
  }
}

function setOrgCanal(profile) {
  if (profile.DatosConfig) {
    if (profile.DatosConfig.ORGANIZACION === "") {
      profile.DatosConfig.ORGANIZACION = profile.DatosConfig.CONFIGURACION.VKORG;
    }
    if (profile.DatosConfig.CANAL === "") {
      profile.DatosConfig.CANAL = profile.DatosConfig.CONFIGURACION.VTWEG;
    }
  }
  return profile;
}

function processAfterReceivePedido(pedido) {
  const bapi = pedido.oClaseBAPI;

  const normalizeList = (listKey, childKey, fallbackKey = null) => {
    let list = bapi[listKey];
    const isEmpty = !list || list === "" || (Array.isArray(list) && list.length === 0);
    if (isEmpty) {
      bapi[listKey] = { [childKey]: [] };
      return;
    }
    if (Array.isArray(list) && list.length > 0 && !list[childKey]) {
      bapi[listKey] = { [childKey]: [...list] };
      return;
    }
    if (list[childKey] && !Array.isArray(list[childKey])) {
      bapi[listKey] = { [childKey]: [{ ...list[childKey] }] };
      return;
    }
    if (fallbackKey && (!list || !list[childKey]) && bapi[fallbackKey]) {
      bapi[listKey] = { [childKey]: [{ ...bapi[fallbackKey] }] };
    }
  };

  normalizeList("listCABIN", "clsZES_CAB_INPUT");
  normalizeList("listCABOUT", "clsZES_CAB_OUTPUT");
  normalizeList("listCABFRM", "clsZES_CAB_FORMA", "CABFRM");
  normalizeList("listOJOIN", "clsZES_OJO_INPUT");
  normalizeList("listOJOOUT", "clsZES_OJO_OUTPUT");
  normalizeList("listFORMAS", "clsZFORMAS");
  normalizeList("listTALADROS", "clsZES_APLT_TLDROS");
  normalizeList("listBASES", "clsZES_BASENOM");

  if (!bapi.listTRETURN || bapi.listTRETURN === "") bapi.listTRETURN = [];

  return pedido;
}

function processBeforeSendPedido(pedido) {
  if (pedido.oClaseBAPI.listCABIN.clsZES_CAB_INPUT.length === 0) pedido.oClaseBAPI.listCABIN = "";
  if (pedido.oClaseBAPI.listCABOUT.clsZES_CAB_OUTPUT.length === 0)
    pedido.oClaseBAPI.listCABOUT = "";
  if (pedido.oClaseBAPI.listCABFRM.clsZES_CAB_FORMA.length === 0) pedido.oClaseBAPI.listCABFRM = "";
  if (pedido.oClaseBAPI.listOJOIN.clsZES_OJO_INPUT.length === 0) pedido.oClaseBAPI.listOJOIN = "";
  if (pedido.oClaseBAPI.listOJOOUT.clsZES_OJO_OUTPUT.length === 0)
    pedido.oClaseBAPI.listOJOOUT = "";
  if (pedido.oClaseBAPI.listFORMAS.clsZFORMAS.length === 0) pedido.oClaseBAPI.listFORMAS = "";
  if (pedido.oClaseBAPI.listTALADROS.clsZES_APLT_TLDROS.length === 0)
    pedido.oClaseBAPI.listTALADROS = "";

  if (pedido.oClaseBAPI.ojoOutIzquierdo) delete pedido.oClaseBAPI.ojoOutIzquierdo;
  if (pedido.oClaseBAPI.ojoOutDerecho) delete pedido.oClaseBAPI.ojoOutDerecho;
  if (pedido.oClaseBAPI.CABFRM) delete pedido.oClaseBAPI.CABFRM;
  if (pedido.oClaseBAPI.CABIN) delete pedido.oClaseBAPI.CABIN;
  if (pedido.oClaseBAPI.CABOUT) delete pedido.oClaseBAPI.CABOUT;

  return pedido;
}

function processBeforeSaveReceta(pedido) {
  const recetaData = {
    CLIENTE: pedido.oClaseBAPI.CLIENTE,
    RECETA: "",
    NUM_REC: "",
    cRETURN: pedido.oClaseBAPI.cRETURN || { NUMBER: 0, LOG_MSG_NO: 0 },
    listCABIN: pedido.oClaseBAPI.listCABIN,
    listCABFRM: pedido.oClaseBAPI.listCABFRM,
    listOJOIN: pedido.oClaseBAPI.listOJOIN,
    listOJOOUT: pedido.oClaseBAPI.listOJOOUT || { clsZES_OJO_OUTPUT: [] },
  };

  if (pedido.oClaseBAPI.listCABOUT?.clsZES_CAB_OUTPUT?.length > 0) {
    recetaData.listCABOUT = pedido.oClaseBAPI.listCABOUT;
  }

  if (recetaData.listCABIN?.clsZES_CAB_INPUT?.length === 0) recetaData.listCABIN = "";
  if (recetaData.listCABFRM?.clsZES_CAB_FORMA?.length === 0) recetaData.listCABFRM = "";
  if (recetaData.listOJOIN?.clsZES_OJO_INPUT?.length === 0) recetaData.listOJOIN = "";
  if (recetaData.listOJOOUT?.clsZES_OJO_OUTPUT?.length === 0) recetaData.listOJOOUT = "";

  if (pedido.oClaseBAPI.listFORMAS?.clsZFORMAS?.length > 0) {
    recetaData.listFORMAS = pedido.oClaseBAPI.listFORMAS;
  } else {
    recetaData.listFORMAS = "";
  }
  if (pedido.oClaseBAPI.listTALADROS?.clsZES_APLT_TLDROS?.length > 0) {
    recetaData.listTALADROS = pedido.oClaseBAPI.listTALADROS;
  } else {
    recetaData.listTALADROS = "";
  }
  recetaData.listTRETURN = "";

  return { oClaseBAPI: recetaData };
}

/** SAP a veces devuelve ORIGEN_DATOS "0" (sin forma) aunque el cliente envió F/M/… */
function origenDatosPerdido(respOrigen, preservedOrigen) {
  const p = String(preservedOrigen ?? "").trim();
  if (!p || p === "0") return false;
  const r = String(respOrigen ?? "").trim();
  return !r || r === "0";
}

function cabCampoPerdido(campo, respVal, preservedVal) {
  const p = preservedVal;
  if (p === null || p === undefined || p === "" || p === "00" || p === "00000") return false;
  const r = respVal;
  if (r === null || r === undefined || r === "") return true;
  if (r === "00" || r === "00000") return true;
  if (["CALIBRE", "PUENTE", "ALT_BOX"].includes(campo) && String(r).trim() === "0") return true;
  return false;
}

/**
 * Campos de `clsZES_CAB_FORMA` que informa el cliente y SAP puede devolver
 * vacíos: se capturan del pedido que se envía y se reponen si la respuesta los
 * pierde. UNA sola lista para PrepararPedido y RealizarPedido — cuando estaban
 * duplicadas divergieron y `MARCA`/`MODELO`/`PROVEEDOR` (datos de la montura de
 * la base de datos de marcas) se perdían en cada cálculo, de forma que el
 * segundo cálculo y el envío final viajaban sin marca ni modelo.
 */
const CAMPOS_FORMA_PRESERVADOS = [
  "CALIBRE",
  "PUENTE",
  "PUENTEORIG",
  "ALT_BOX",
  "DIST_NP_OD",
  "DIST_NP_OI",
  "ALTURA_OD",
  "ALTURA_OI",
  "TIPO_MONTURA",
  "BISEL",
  "ORIENTACION",
  "CLAVEMONTURA",
  "MARCA",
  "MODELO",
  "PROVEEDOR",
  "EAN11",
  "COLOR_TXT",
];

/** Copia de la fila de forma del pedido que se está enviando, para repararla al volver. */
function snapshotCabForma(cab) {
  if (!cab) return null;
  const snap = { ORIGEN_DATOS: cab.ORIGEN_DATOS || "", FORMA_BASICA: cab.FORMA_BASICA };
  for (const campo of CAMPOS_FORMA_PRESERVADOS) snap[campo] = cab[campo];
  return snap;
}

/**
 * Repone en la respuesta de SAP los campos de forma que ha devuelto vacíos,
 * incluido `ORIGEN_DATOS` (que tiene su propia noción de "perdido": el "0" de
 * SAP). `FORMA_BASICA` NO entra aquí: solo el cálculo la repone.
 */
function restoreCamposForma(cabResp, pres) {
  if (!cabResp || !pres) return;
  if (origenDatosPerdido(cabResp.ORIGEN_DATOS, pres.ORIGEN_DATOS))
    cabResp.ORIGEN_DATOS = pres.ORIGEN_DATOS;
  for (const campo of CAMPOS_FORMA_PRESERVADOS) {
    if (cabCampoPerdido(campo, cabResp[campo], pres[campo])) cabResp[campo] = pres[campo];
  }
}

/** Con errores SAP, `clsZES_CAB_FORMA` puede ser [] y no hay [0] para fusionar montura/bisel/origen. */
function ensureListCabFormaFirstRow(bapi) {
  if (!bapi) return;
  let list = bapi.listCABFRM;
  if (!list || list === "") {
    bapi.listCABFRM = { clsZES_CAB_FORMA: [{}] };
    return;
  }
  const arr = list.clsZES_CAB_FORMA;
  if (!arr || !Array.isArray(arr)) {
    bapi.listCABFRM = { clsZES_CAB_FORMA: [{}] };
    return;
  }
  if (arr.length === 0) arr.push({});
}

/** Si SAP devuelve error (listTRETURN E o returnValue≠0), reponer CABFRM del envío para no vaciar el store. */
function overlayPreservedCabFormaIfSapError(cab, pres) {
  if (!cab || !pres) return;
  for (const k of Object.keys(pres)) {
    const v = pres[k];
    if (v === null || v === undefined) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    cab[k] = v;
  }
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useIndoStore = defineStore("indo", () => {
  // ── State ──────────────────────────────────────────────────────────────────
  /** Evita varias PostLogin en paralelo (refresh → Header + vistas); una falla podía hacer Logout antes de que otra terminara. */
  let postLoginInFlight = null;

  const profile = ref(null);
  const erroresPedido = ref([]);
  const warningsPedido = ref([]);
  /** Pedidos para dashboard (widget / GetPedidos con destino home) */
  const pedidosData = ref({});
  /** Pedidos para la vista /lentes/pedidos (filtros, tabla); no mezclar con home */
  const pedidosFiltradosData = ref({});
  const pedidoRef = ref({});
  const comerciales = ref([]);
  const comercialPhotos = ref({});
  const contactosATC = ref([]);
  const recetas = ref(null);
  /** Último rango de fechas usado en GetRecetas (evita refetch al volver a la vista) */
  const lastRecetasFetchRange = ref(null);
  const mensajeRecetas = ref(null);
  const detallesObject = ref(null);
  const alertas = ref([]);
  /** Importe pendiente de facturar por marca: { [brand]: sRetorno } */
  const importePendienteFacturar = ref({});
  /** Dashboard: evita refetch en cada visita (logout → emptyData resetea) */
  const dashboardHomePedidosFetchedAt = ref(0);
  const comercialFotosFetchedAt = ref(0);
  const preferredOrgVenta = ref({
    ORG_VENTA: localStorage.getItem("preferredOrgVenta") || "",
    TEXTO_ORGANIZACION: localStorage.getItem("preferredOrgVentaTexto") || "",
    JERARQUIA_SUPERIOR: "",
    JERARQUIA_L: "",
    CANAL_DIST: "",
    SECTOR: "",
  });

  // ── Getters (computed) ─────────────────────────────────────────────────────
  // En modo Optovisión el usuario solo puede ver pedidos de Optovisión.
  // setProfile ya restringe listaAreasCliente a la marca Optovisión, así que de
  // ahí salen los VKORG permitidos. El dashboard/home pide con VKORG vacío → el
  // backend devuelve pedidos de TODAS las organizaciones del usuario; este guardo
  // descarta cualquier pedido ajeno antes de pintarlo en las tablas.
  const OPTOVISION_MODE = import.meta.env.VITE_MODO_OPTOVISION === "true";
  const normalizeVkorg = (v) => String(v ?? "").replace(/^0+/, "");
  function filterOptovisionPedidos(list) {
    if (!OPTOVISION_MODE || !Array.isArray(list)) return list;
    const areas = profile.value?.AreaVenta?.listaAreasCliente || [];
    const allowed = new Set(areas.map((a) => normalizeVkorg(a.ORG_VENTA)));
    if (allowed.size === 0) return [];
    return list.filter((p) => allowed.has(normalizeVkorg(p.VKORG)));
  }

  const pedidos = computed(() => {
    const d = pedidosFiltradosData.value;
    if (!d?.oClaseBAPI) return null;
    const out = { ...d.oClaseBAPI };
    if (Array.isArray(out.listLISTAPEDIDOS)) {
      out.listLISTAPEDIDOS = filterOptovisionPedidos(out.listLISTAPEDIDOS);
    }
    if (d.filtrosListaPedidos !== undefined) {
      out.filtrosListaPedidos = d.filtrosListaPedidos;
    }
    return out;
  });
  const pedidosFiltrados = computed(() => {
    const list = pedidosFiltradosData.value?.oClaseBAPI?.listLISTAPEDIDOS;
    return Array.isArray(list) ? filterOptovisionPedidos([...list]) : null;
  });
  const homePedidos = computed(() => {
    const list = pedidosData.value?.oClaseBAPI?.listLISTAPEDIDOS ?? null;
    return Array.isArray(list) ? filterOptovisionPedidos(list) : list;
  });
  /**
   * Opciones de marca (organización de ventas) del cliente para los selectores,
   * una por ORG_VENTA aunque el perfil la repita con otro canal/sector.
   */
  const brandOptions = computed(() => {
    const byOrg = new Map();
    (profile.value?.AreaVenta?.listaAreasCliente || []).forEach((area) => {
      if (!area?.ORG_VENTA || byOrg.has(area.ORG_VENTA)) return;
      byOrg.set(area.ORG_VENTA, {
        value: area.ORG_VENTA,
        label: area.TEXTO_ORGANIZACION ? area.TEXTO_ORGANIZACION.toUpperCase() : "INDO",
      });
    });
    return Array.from(byOrg.values());
  });
  const getComerciales = computed(() => profile.value?.AreaVenta?.listaComerciales || []);
  const suGestor = computed(() => profile.value?.AreaVenta?.datosCliente?.SU_GESTOR || null);
  /** Moneda del cliente (DatosConfig.DETALLES.MONEDA_CLI): "EUR" | "MAD"; EUR por defecto. */
  const monedaCliente = computed(() =>
    normalizeCurrencyCode(profile.value?.DatosConfig?.DETALLES?.MONEDA_CLI),
  );
  const simboloMoneda = computed(() => getCurrencySymbol(monedaCliente.value));
  const recetasList = computed(() => recetas.value?.oClaseBAPI?.listRECETAS || []);

  // ── Mutations (now plain functions) ───────────────────────────────────────
  /**
   * Marca (organización de ventas) global: la comparten el selector de la
   * cabecera y los filtros de marca de cada pantalla. Solo acepta ORG_VENTA
   * presentes en `listaAreasCliente`.
   */
  function setPreferredOrgVenta(orgVenta) {
    const area = (profile.value?.AreaVenta?.listaAreasCliente || []).find(
      (a) => a.ORG_VENTA === orgVenta,
    );
    if (!area) return;
    // Los filtros la llaman en cada emit: no invalidar dependientes si no cambia.
    const current = preferredOrgVenta.value;
    if (current?.ORG_VENTA === area.ORG_VENTA && current.TEXTO_ORGANIZACION === area.TEXTO_ORGANIZACION) return;
    localStorage.setItem("preferredOrgVenta", area.ORG_VENTA);
    preferredOrgVenta.value = {
      ORG_VENTA: area.ORG_VENTA || "",
      TEXTO_ORGANIZACION: area.TEXTO_ORGANIZACION || "",
      JERARQUIA_SUPERIOR: area.JERARQUIA_SUPERIOR || "",
      JERARQUIA_L: area.JERARQUIA_L || "",
      CANAL_DIST: area.CANAL_DIST || "",
      SECTOR: area.SECTOR || "",
    };
  }

  function setProfile(data) {
    if (import.meta.env.VITE_MODO_OPTOVISION === "true" && data?.AreaVenta?.listaAreasCliente) {
      const optovisionBrand = data.AreaVenta.listaAreasCliente.find((brand) => {
        const texto = (brand.TEXTO_ORGANIZACION || "").toLowerCase();
        return texto.includes("optovision") || texto.includes("optvision");
      });
      if (optovisionBrand) {
        const optovisionVkorg = optovisionBrand.ORG_VENTA;
        data.AreaVenta.listaAreasCliente = [optovisionBrand];
        data.AreaVenta.ORG_VENTA = optovisionVkorg;
        if (data.DatosConfig?.CONFIGURACION) data.DatosConfig.CONFIGURACION.VKORG = optovisionVkorg;
        if (data.DatosConfig?.ORGANIZACION !== undefined)
          data.DatosConfig.ORGANIZACION = optovisionVkorg;
      } else {
        data.AreaVenta.listaAreasCliente = [];
        data.AreaVenta.listaComerciales = [];
      }
      if (optovisionBrand && data?.AreaVenta?.listaComerciales) {
        const filtered = data.AreaVenta.listaComerciales.filter(
          (c) => c.VKORG && c.VKORG.toString() === optovisionBrand.ORG_VENTA,
        );
        if (filtered.length > 0) data.AreaVenta.listaComerciales = filtered;
      }
    }
    normalizeDatosClienteHtmlEntities(data.AreaVenta);
    profile.value = setOrgCanal(data);
    comerciales.value = data?.AreaVenta?.listaComerciales || [];
  }

  function setDatosConfig(data) {
    if (!profile.value) return;
    if (!profile.value.DatosConfig) profile.value.DatosConfig = {};

    const preservedFavoritos = profile.value.DatosConfig.listaFavoritos
      ? JSON.parse(JSON.stringify(profile.value.DatosConfig.listaFavoritos))
      : null;
    const preservedNoFavoritos = profile.value.DatosConfig.listaNoFavoritos
      ? JSON.parse(JSON.stringify(profile.value.DatosConfig.listaNoFavoritos))
      : null;

    profile.value.DatosConfig = { ...profile.value.DatosConfig, ...data };

    if (preservedFavoritos) {
      if (
        profile.value.DatosConfig.listaFavoritos?.clsFavoritos &&
        Array.isArray(profile.value.DatosConfig.listaFavoritos.clsFavoritos)
      ) {
        const existing = profile.value.DatosConfig.listaFavoritos.clsFavoritos;
        const existingMap = new Map(
          existing.filter((f) => f.JER_EDI_NET).map((f) => [f.JER_EDI_NET, f]),
        );
        (preservedFavoritos.clsFavoritos || []).forEach((f) => {
          if (f.JER_EDI_NET && !existingMap.has(f.JER_EDI_NET)) existing.push(f);
        });
      } else {
        profile.value.DatosConfig.listaFavoritos = preservedFavoritos;
      }
    } else if (!profile.value.DatosConfig.listaFavoritos) {
      profile.value.DatosConfig.listaFavoritos = { clsFavoritos: [] };
    }

    if (preservedNoFavoritos) {
      if (
        profile.value.DatosConfig.listaNoFavoritos?.clsFavoritos &&
        Array.isArray(profile.value.DatosConfig.listaNoFavoritos.clsFavoritos)
      ) {
        const existing = profile.value.DatosConfig.listaNoFavoritos.clsFavoritos;
        const existingMap = new Map(
          existing.filter((f) => f.JER_EDI_NET).map((f) => [f.JER_EDI_NET, f]),
        );
        (preservedNoFavoritos.clsFavoritos || []).forEach((f) => {
          if (f.JER_EDI_NET && !existingMap.has(f.JER_EDI_NET)) existing.push(f);
        });
      } else {
        profile.value.DatosConfig.listaNoFavoritos = preservedNoFavoritos;
      }
    } else if (!profile.value.DatosConfig.listaNoFavoritos) {
      profile.value.DatosConfig.listaNoFavoritos = { clsFavoritos: [] };
    }

    profile.value = setOrgCanal(profile.value);
  }

  function setErroresPedidoRaw(data) {
    if (data.length > 0) {
      erroresPedido.value = data.map((t) => {
        let msg = t.TYPE === "E" ? "Error: " : t.TYPE === "W" ? "Atención: " : "Información: ";
        return `${msg}${t.MESSAGE} ${t.MESSAGE_V1 ?? ""} ${t.MESSAGE_V2 ?? ""} ${t.MESSAGE_V3 ?? ""} ${t.MESSAGE_V4 ?? ""}`;
      });
    }
  }

  function setWarningsPedidoRaw(data) {
    if (data.length === 0) {
      warningsPedido.value = [];
      return;
    }
    warningsPedido.value = data.map((t) => {
      if (typeof t === "string") return t;
      let msg = t.TYPE === "W" ? "Atención: " : "Información: ";
      const parts = [t.MESSAGE, t.MESSAGE_V1, t.MESSAGE_V2, t.MESSAGE_V3, t.MESSAGE_V4].filter(
        (p) => p?.trim(),
      );
      return `${msg}${parts.join(" ")}`;
    });
  }

  function emptyData() {
    profile.value = null;
    pedidosData.value = {};
    pedidosFiltradosData.value = {};
    pedidoRef.value = {};
    erroresPedido.value = [];
    warningsPedido.value = [];
    comercialPhotos.value = {};
    recetas.value = null;
    mensajeRecetas.value = null;
    lastRecetasFetchRange.value = null;
    dashboardHomePedidosFetchedAt.value = 0;
    comercialFotosFetchedAt.value = 0;
    importePendienteFacturar.value = {};
  }

  function markHomePedidosFetched() {
    dashboardHomePedidosFetchedAt.value = Date.now();
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  async function PostLogin() {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    if (!auth.token) return;
    if (profile.value) return;
    if (postLoginInFlight) return postLoginInFlight;

    postLoginInFlight = (async () => {
      try {
        const data = await requests.postLogin(auth.username, auth.token);
        const postData = response.postLogin(data);

        if (postData.returnValue === 0) {
          setProfile(postData);

          const orgVenta = postData.AreaVenta?.ORG_VENTA;
          let vkorgForLocale = orgVenta;
          if (orgVenta) {
            const existingPreferred = localStorage.getItem("preferredOrgVenta");
            const isValidExisting =
              existingPreferred &&
              postData.AreaVenta?.listaAreasCliente?.some((a) => a.ORG_VENTA === existingPreferred);
            const finalOrg = isValidExisting ? existingPreferred : orgVenta;
            localStorage.setItem("preferredOrgVenta", finalOrg);
            vkorgForLocale = finalOrg;
            setPreferredOrgVenta(finalOrg);
          }
          syncLocaleAfterLogin(vkorgForLocale);
        } else {
          await useAuthStore().Logout();
          if (typeof window !== "undefined") window.location.href = "/login";
        }
      } finally {
        postLoginInFlight = null;
      }
    })();

    return postLoginInFlight;
  }

  async function GetContactosATC() {
    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      const data = await requests.getContactosATC(auth.token);
      const contactosData = response.GetContactosATCResult(data);
      if (contactosData?.ContactosATC) contactosATC.value = contactosData.ContactosATC;
    } catch (error) {
    }
  }

  /**
   * @param {object} fechas - fechaIni, fechaFin, marca
   * @param {{ destino?: 'home' | 'orders'; forceRefresh?: boolean; maxAgeMs?: number }} [options] - home = dashboard; orders = lista filtrada (default)
   */
  async function GetPedidos(fechas, options = {}) {
    const destino = options.destino === "home" ? "home" : "orders";
    const targetRef = destino === "home" ? pedidosData : pedidosFiltradosData;
    const forceRefresh = options.forceRefresh === true;
    const maxAgeMs = typeof options.maxAgeMs === "number" ? options.maxAgeMs : 5 * 60 * 1000;

    if (destino === "home" && !forceRefresh && dashboardHomePedidosFetchedAt.value > 0) {
      const age = Date.now() - dashboardHomePedidosFetchedAt.value;
      const list = pedidosData.value?.oClaseBAPI?.listLISTAPEDIDOS;
      if (age < maxAgeMs && list !== undefined) {
        return true;
      }
    }

    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      let {
        fechaIni = null,
        fechaFin = null,
        marca = null,
        referencia = null,
        producto = null,
        numPedido = null,
        numAlbaran = null,
        esfera = null,
        cilindro = null,
        adicion = null,
        grabado = null,
        estado = null,
        todosLosSolicitantes = false,
      } = fechas || {};
      const extras = {
        referencia: referencia || "",
        producto: producto || "",
        numPedido: numPedido || "",
        numAlbaran: numAlbaran || "",
        esfera: esfera || "",
        cilindro: cilindro || "",
        adicion: adicion || "",
        grabado: grabado || "T",
        estado: estado || "",
        todosLosSolicitantes: !!todosLosSolicitantes,
      };

      if (!auth.username || !auth.token) await PostLogin();
      if (!profile.value) await PostLogin();

      // `DIAS_PTE_VL` (días pendientes por defecto) llega por usuario desde SAP y
      // puede faltar (null/""/0/inexistente). Antes, si faltaba, se machacaban
      // SIEMPRE fechaIni/fechaFin con un mes desde hoy, ignorando las fechas que
      // llegaban del filtro → algunos usuarios veían solo el último mes sin razón.
      // Ahora las fechas recibidas se respetan siempre; el rango por defecto
      // (DIAS_PTE_VL, o 7 días como fallback) solo se aplica cuando no llegan.
      const diasDefecto = Number(profile.value?.DatosConfig?.DETALLES?.DIAS_PTE_VL) || 7;
      if (fechaIni === null || fechaIni === undefined) {
        fechaIni = moment().subtract(diasDefecto, "days").toDate();
      }
      if (fechaFin === null || fechaFin === undefined) {
        fechaFin = moment().toDate();
      }

      let listaAreasCliente = profile.value?.AreaVenta?.listaAreasCliente || [];
      if (marca) listaAreasCliente = listaAreasCliente.filter((area) => area.ORG_VENTA === marca);

      const filtrosListaPedidos =
        destino === "orders"
          ? {
              fechaIni: fechaIni ? moment(fechaIni).format("DD/MM/YYYY") : null,
              fechaFin: fechaFin ? moment(fechaFin).format("DD/MM/YYYY") : null,
              marca: marca || null,
              grabado: extras.grabado,
              estado: extras.estado,
              todosLosSolicitantes: !!todosLosSolicitantes,
            }
          : undefined;

      // home o marca vacía: una única llamada con VKORG/VTWEG/SPART vacíos — el backend
      // devuelve los pedidos de todas las organizaciones del usuario.
      // orders con marca: una llamada por cada área del cliente (filtrada por la marca).
      const areasAConsultar =
        destino === "home" || !marca
          ? [{ ORG_VENTA: "", CANAL_DIST: "", SECTOR: "" }]
          : listaAreasCliente;

      if (areasAConsultar.length === 0) {
        targetRef.value =
          destino === "orders"
            ? {
                oClaseBAPI: { listLISTAPEDIDOS: [] },
                filtrosListaPedidos,
              }
            : { oClaseBAPI: { listLISTAPEDIDOS: [] } };
        if (destino === "home") markHomePedidosFetched();
        return false;
      }

      const allPedidos = [];
      let firstPostData = null;
      let hasError = false;

      for (let i = 0; i < areasAConsultar.length; i++) {
        const areaCliente = areasAConsultar[i];
        try {
          if (i > 0) await new Promise((resolve) => setTimeout(resolve, 500));

          const areaProfile = JSON.parse(JSON.stringify(profile.value));
          if (areaProfile.AreaVenta) {
            areaProfile.AreaVenta.ORG_VENTA = areaCliente.ORG_VENTA;
            areaProfile.AreaVenta.CANAL_DIST = areaCliente.CANAL_DIST;
            areaProfile.AreaVenta.SECTOR = areaCliente.SECTOR;
          }

          let areaData = null;
          let areaPostData = null;
          const maxRetries = 2;

          for (let retry = 0; retry <= maxRetries; retry++) {
            if (retry > 0) await new Promise((resolve) => setTimeout(resolve, 1000 * retry));

            areaData = await requests.getPedidos(
              auth.username,
              auth.token,
              areaProfile,
              moment(fechaIni).format("DD/MM/YYYY"),
              moment(fechaFin).format("DD/MM/YYYY"),
              extras,
            );

            if (areaData) {
              areaPostData = response.pedidos(areaData);
              if (areaPostData.returnValue === 0) break;
            }
          }

          if (areaData && areaPostData) {
            if (!firstPostData) firstPostData = areaPostData;
            if (areaPostData.oClaseBAPI?.listLISTAPEDIDOS) {
              allPedidos.push(...areaPostData.oClaseBAPI.listLISTAPEDIDOS);
            }
            if (areaPostData.returnValue !== 0) hasError = true;
          }
        } catch (error) {
          // Token caducado: el interceptor ya está cerrando la sesión; no
          // tiene sentido seguir consultando el resto de áreas.
          if (isSessionExpiredError(error)) throw error;
          hasError = true;
        }
      }

      const combinedData = {
        ...(firstPostData || {}),
        oClaseBAPI: { ...(firstPostData?.oClaseBAPI || {}), listLISTAPEDIDOS: allPedidos },
      };

      if (!firstPostData) {
        targetRef.value =
          destino === "orders"
            ? {
                oClaseBAPI: { listLISTAPEDIDOS: [] },
                error: "No se recibieron datos del servidor",
                filtrosListaPedidos,
              }
            : {
                oClaseBAPI: { listLISTAPEDIDOS: [] },
                error: "No se recibieron datos del servidor",
              };
        if (destino === "home") markHomePedidosFetched();
        return false;
      }
      if (hasError && allPedidos.length === 0) {
        targetRef.value =
          destino === "orders"
            ? {
                oClaseBAPI: { listLISTAPEDIDOS: [] },
                error: firstPostData.MensajeErrorSAP || "Error obteniendo los pedidos",
                filtrosListaPedidos,
              }
            : {
                oClaseBAPI: { listLISTAPEDIDOS: [] },
                error: firstPostData.MensajeErrorSAP || "Error obteniendo los pedidos",
              };
        if (destino === "home") markHomePedidosFetched();
        return false;
      }

      targetRef.value =
        destino === "orders" ? { ...combinedData, filtrosListaPedidos } : combinedData;
      if (destino === "home") markHomePedidosFetched();
      return true;
    } catch (error) {
      if (isSessionExpiredError(error)) return false;
      targetRef.value =
        destino === "orders"
          ? {
              oClaseBAPI: { listLISTAPEDIDOS: [] },
              error: error.message || "Error desconocido",
              filtrosListaPedidos: null,
            }
          : {
              oClaseBAPI: { listLISTAPEDIDOS: [] },
              error: error.message || "Error desconocido",
            };
      if (destino === "home") markHomePedidosFetched();
      return false;
    }
  }

  async function GetPedidoByNumero(payload) {
    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      if (!auth.username || !auth.token) await PostLogin();
      if (!profile.value) await PostLogin();

      const numeroPedido = typeof payload === "string" ? payload : payload.numeroPedido;
      const vkorg = typeof payload === "object" ? payload.vkorg : null;
      const vtweg = typeof payload === "object" ? payload.vtweg : null;
      const spart = typeof payload === "object" ? payload.spart : null;
      const year = typeof payload === "object" ? payload.year : null;
      const refPedido = typeof payload === "object" ? payload.refPedido : null;

      const data = await requests.getPedidoByNumero(
        auth.token,
        profile.value,
        numeroPedido,
        vkorg,
        vtweg,
        spart,
        year,
        refPedido,
      );
      if (!data) throw new Error("No se recibieron datos del servidor");

      const postData = response.recuperarPedido(data);
      switch (postData.returnValue) {
        case 0:
          return postData;
        default:
          throw new Error(postData.MensajeErrorSAP || "Error obteniendo el detalle del pedido");
      }
    } catch (error) {
      throw error;
    }
  }

  async function GetNuevoPedido() {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    if (!auth.token) await PostLogin();
    if (!profile.value) await PostLogin();
    const data = await requests.getNuevoPedido(auth.token);
    const postData = response.nuevoPedido(data);
    const p = new XMLParser({ numberParseOptions: { leadingZeros: false } });
    const parsed = p.parse("<oClaseBAPI>" + postData + "</oClaseBAPI>");
    const pedido = parsed.oClaseBAPI;
    const cabin = pedido.listCABIN?.clsZES_CAB_INPUT;
    const cabinInput = cabin ? (Array.isArray(cabin) ? cabin[0] : cabin) : null;

    const areaCliente = preferredOrgVenta.value?.ORG_VENTA ? preferredOrgVenta.value : profile.value.AreaVenta.listaAreasCliente[0];
    const prof = profile.value;

    pedido.EDITABLE = prof.AreaVenta.TIPO_CLIENTE !== "J";
    pedido.CLIENTE = prof.DatosConfig.CUSTOMERNO;
    pedido.VKORG = areaCliente.ORG_VENTA;
    pedido.VTWEG = areaCliente.CANAL_DIST;
    pedido.SPART = areaCliente.SECTOR;

    if (cabinInput) {
      cabinInput.KUNNR = prof.DatosConfig.CLIENTESAP;
      cabinInput.TIPO_POS_USO = prof.DatosConfig.CONFIGURACION.TIPO_POS_USO;
      cabinInput.VKORG = areaCliente.ORG_VENTA;
      cabinInput.VTWEG = areaCliente.CANAL_DIST;
      cabinInput.SPART = areaCliente.SECTOR;
      cabinInput.VKORG_DESCR = areaCliente.TEXTO_ORGANIZACION.toUpperCase();
      cabinInput.BISEL = "";
      cabinInput.MONTAJE = "";
    }

    if (pedido.listCABFRM?.clsZES_CAB_FORMA) {
      const cabForma = Array.isArray(pedido.listCABFRM.clsZES_CAB_FORMA)
        ? pedido.listCABFRM.clsZES_CAB_FORMA[0]
        : pedido.listCABFRM.clsZES_CAB_FORMA;
      if (cabForma) cabForma.TIPO_ALTURA = prof.DatosConfig.CONFIGURACION.TIPO_ALT;
    }

    const processedData = processAfterReceivePedido(parsed);
    erroresPedido.value = [];
    warningsPedido.value = [];
    pedidoRef.value = processedData;

    const { usePedidosStore } = await import("./pedidos");
    const pedidosStore = usePedidosStore();
    pedidosStore.setBisel(false);
    pedidosStore.setMontaje(false);
    pedidosStore.setReferencia("");
    pedidosStore.setCodigoPromocional("");

    return processedData;
  }

  async function SaveAjustes({ datosConfig }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const payload = datosConfig.oClaseBAPI ? datosConfig : { oClaseBAPI: datosConfig };
    setDatosConfig(payload.oClaseBAPI);
    const data = await requests.saveAjustes(json2xml(payload), auth.token);
    response.ajustes(data);
  }

  async function RecuperarDatosOptica() {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const prof = profile.value;
    const data = await requests.recuperarDatosOptica(
      prof.AreaVenta.CLIENTE,
      prof.AreaVenta.ORG_VENTA,
      prof.AreaVenta.CANAL_DIST,
      auth.token,
    );
    const postData = response.recuperarDatosOptica(data);
    setDatosConfig(postData.DatosConfig);
    return postData;
  }

  async function SaveMailbox({ datosConfig }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const data = await requests.saveMailbox(json2xml({ oClaseBAPI: datosConfig }), auth.token);
    const postData = response.buzon(data);
    return postData.returnValue !== 0 ? postData.MensajeErrorSAP : "";
  }

  async function ManagerBuscarFacturas(fechas) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    let dates = fechas;
    if (!dates) {
      dates = {
        from: moment().subtract(24, "months").format("YYYY-MM-DD"),
        to: moment().format("YYYY-MM-DD"),
        noclient: "",
        noalb: "",
        nofact: "",
        tipo: "T",
      };
    }
    if (dates.from === null) dates.from = moment().subtract(4, "years").format("YYYY-MM-DD");
    if (dates.to === null) dates.to = moment().format("YYYY-MM-DD");
    const { from, to, noclient, nofact, noalb, tipo = "T" } = dates;
    const data = await requests.searchManagerFacturas(
      auth.tokenManager,
      auth.indoManager,
      from,
      to,
      tipo,
      noclient,
      nofact,
      noalb,
    );
    const postData = response.searchManagerFacturas(data);
    if (postData.returnValue !== 0) throw new Error(postData.ExportReturnMessage);
    return postData.oClaseBAPI.reverse();
  }

  async function ManagerBuscarPendientesFacturar(solicitante = "", brand = "") {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const data = await requests.searchPendientesFacturar(
      solicitante,
      auth.tokenManager,
      auth.indoManager,
    );
    const postData = response.searchPendientesFacturar(data);
    if (postData.returnValue !== 0)
      throw new Error(postData.ExportReturnMessage || postData.MensajeErrorSAP);
    if (!postData.oClaseBAPI?.listaPendienteFacturar) {
      if (brand) importePendienteFacturar.value[brand] = "";
      return { facturas: [], sRetorno: "" };
    }
    const facturas = postData.oClaseBAPI.listaPendienteFacturar;
    const sRetorno = postData.oClaseBAPI?.Respuesta?.sRetorno || "";
    if (brand) importePendienteFacturar.value[brand] = sRetorno;
    if (!Array.isArray(facturas)) return { facturas: [], sRetorno };
    return { facturas: facturas.reverse(), sRetorno };
  }

  async function ManagerGetBonos(solicitante = "") {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const data = await requests.getBonos(
      auth.username,
      auth.tokenManager,
      auth.indoManager,
      solicitante,
    );
    const postData = response.bonos(data);
    if (postData.returnValue !== 0) throw new Error(postData.ExportReturnMessage);
    return postData.oClaseBAPI;
  }

  async function ManagerBuscarListaClientes() {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const data = await requests.buscarListaClientes(auth.tokenManager, auth.indoManager);
    const postData = response.buscarListaClientes(data);
    if (postData.returnValue !== 0) throw new Error(postData.ExportReturnMessage);
    return postData.oClaseBAPI.listaSolicitantes;
  }

  async function ManagerGetFacturaSap({ factura, fechaFactura, pagador = "" }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    // El pagador/solicitante debe ser el cliente de la factura (columna PAGADOR
    // de la fila), no el clienteSap del usuario logueado. Sólo se recurre al
    // clienteSap del perfil si no llega pagador.
    let cliente = pagador;
    if (!cliente) {
      if (!profile.value) await PostLogin();
      cliente = profile.value.AreaVenta.CLIENTESAP;
    }
    const data = await requests.getFacturaSap(
      cliente,
      auth.tokenManager,
      cliente,
      factura,
      fechaFactura,
    );
    const postData = response.facturaSap(data);
    if (postData.returnValue !== 0)
      throw new Error(postData.ExportReturnMessage || postData.MensajeErrorSAP);
    return postData;
  }

  async function ManagerAbrirReclamacionFactura({ cliente, nombreCliente, factura, fechaFactura, observaciones }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const data = await requests.abrirReclamacionFactura(
      auth.tokenManager,
      String(cliente).replace(/^0+/, ""),
      nombreCliente,
      factura,
      fechaFactura,
      observaciones,
    );
    const postData = response.abrirReclamacionFactura(data);
    if (postData.returnValue !== 0)
      throw new Error(postData.MensajeError || "Error al abrir reclamación");
    return postData;
  }

  async function ManagerRecuperarAlbaran({ numeroAlbaran }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const data = await requests.recuperarAlbaran(
      numeroAlbaran,
      auth.username,
      auth.indoManager,
      auth.tokenManager,
    );
    const postData = response.recuperarAlbaran(data);
    if (postData.error) throw new Error(postData.error);
    if (postData.returnValue && postData.returnValue !== 0)
      throw new Error(
        postData.MensajeErrorSAP || postData.ExportReturnMessage || "Error al recuperar el albarán",
      );
    return postData;
  }

  async function ManagerGetBonoDetalle({ bono }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const data = await requests.getBonoDetalle(auth.tokenManager, auth.indoManager, bono);
    const postData = response.bonoDetalle(data);
    if (postData.returnValue !== 0) throw new Error(postData.ExportReturnMessage);
    return postData.oClaseBAPI;
  }

  async function ManagerGetAllBonoDetalle({ bonos }) {
    const data = await Promise.all(
      bonos.map(async (bono) => await ManagerGetBonoDetalle({ bono })),
    );
    let ret = [];
    data.forEach((d) => {
      ret = [...d.listaRespuestaDetallePuntosBonos, ...ret];
    });
    return ret;
  }

  async function ManagerGetServicioEstadisticas(fechas) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const [dataS, dataC] = await Promise.all([
      requests.getEstadisticaServicio(auth.tokenManager, auth.indoManager, fechas),
      requests.getEstadisticaConsumo(auth.tokenManager, auth.indoManager, fechas),
    ]);
    const postDataS = response.estadisticaServicio(dataS);
    const postDataC = response.estadisticaConsumo(dataC);

    if (postDataS.returnValue !== 0 && postDataC.returnValue !== 0)
      throw new Error("Error al obtener estadísticas: ambos endpoints fallaron");

    const resultado = {};
    if (postDataC.returnValue === 0 && postDataC.oClaseBAPI) {
      Object.entries(postDataC.oClaseBAPI).forEach(([k, v]) => {
        if (v !== undefined && v !== null) resultado[k] = v;
      });
    }
    if (postDataS.returnValue === 0 && postDataS.oClaseBAPI) {
      Object.entries(postDataS.oClaseBAPI).forEach(([k, v]) => {
        if (v !== undefined && v !== null) resultado[k] = v;
      });
    }
    return resultado;
  }

  async function ManagerSendEmail347(anyo) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    if (!profile.value) await PostLogin();
    const cliente = profile.value.AreaVenta.datosCliente.CLIENTE;
    const res = await requests.requestInformacionFiscal(cliente, "347", auth.tokenManager, anyo);
    // TODO: remove debug alert
    if (res.returnValue != 0)
      throw new Error(res.ErrorMessage || res.mensajeError || res.MensajeError || res.MensajeErrorSAP || "Error");
    return null;
  }

  async function ManagerSendEmailFacturas(anyo) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    if (!profile.value) await PostLogin();
    const cliente = profile.value.AreaVenta.datosCliente.CLIENTE;
    const res = await requests.requestInformacionFiscal(cliente, "FACTURAS", auth.tokenManager, anyo);
    if (res.returnValue != 0)
      throw new Error(res.ErrorMessage || res.mensajeError || res.MensajeError || res.MensajeErrorSAP || "Error");
    return null;
  }

  async function GetBases({ pedido }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const data = await requests.getBases(auth.token, json2xml(pedido));
    const postData = response.getBases(data);
    if (postData.returnValue !== 0) throw new Error(postData.MensajeErrorSAP);
    return processAfterReceivePedido(postData).oClaseBAPI;
  }

  async function PrepararPedido({ pedido }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();

    const cabFormaAntes = pedido?.listCABFRM?.clsZES_CAB_FORMA?.[0] ?? pedido?.listCABFRM?.[0];
    const listFORMASPreservada =
      pedido?.listFORMAS &&
      typeof pedido.listFORMAS === "object" &&
      Array.isArray(pedido.listFORMAS?.clsZFORMAS) &&
      pedido.listFORMAS.clsZFORMAS.length > 0
        ? JSON.parse(JSON.stringify(pedido.listFORMAS))
        : null;
    const formaPreservada = snapshotCabForma(cabFormaAntes);

    const cabinAntes =
      pedido?.listCABIN?.clsZES_CAB_INPUT?.[0] ||
      (Array.isArray(pedido?.listCABIN) ? pedido.listCABIN[0] : null);
    const montajePreservado = cabinAntes?.MONTAJE || "";
    const biselCabinPreservado = cabinAntes?.BISEL || "";

    if (pedido.VKORG != "0803") pedido.EsPedidoINDO = false;
    const processedOrder = { oClaseBAPI: { ...pedido } };
    const xml = json2xml(processBeforeSendPedido(processedOrder).oClaseBAPI);

    const idioma = getSoapIdioma();

    const data = await requests.prepararPedido(auth.token, xml, idioma);
    let postData = response.prepararPedido(data);

    erroresPedido.value = [];
    const processedData = processAfterReceivePedido(postData);

    if (formaPreservada) ensureListCabFormaFirstRow(processedData.oClaseBAPI);

    // Restore forma data
    if (formaPreservada) {
      const origenP = String(formaPreservada.ORIGEN_DATOS || "").trim();
      const respList = processedData.oClaseBAPI?.listFORMAS;
      const forMasVacío =
        !respList ||
        respList === "" ||
        (typeof respList === "object" && !Array.isArray(respList?.clsZFORMAS)) ||
        (Array.isArray(respList?.clsZFORMAS) && respList.clsZFORMAS.length === 0);
      if (listFORMASPreservada && (origenP === "F" || origenP === "B") && forMasVacío) {
        processedData.oClaseBAPI.listFORMAS = listFORMASPreservada;
      }
      const cabFormaResp = processedData.oClaseBAPI?.listCABFRM?.clsZES_CAB_FORMA?.[0];
      if (cabFormaResp) {
        const preservedFb = Number(formaPreservada.FORMA_BASICA);
        const respFb = Number(cabFormaResp.FORMA_BASICA);
        if (
          Number.isFinite(preservedFb) &&
          preservedFb > 0 &&
          (!Number.isFinite(respFb) || respFb === 0)
        ) {
          cabFormaResp.FORMA_BASICA = formaPreservada.FORMA_BASICA;
        }
        restoreCamposForma(cabFormaResp, formaPreservada);
      }
    }

    const errorsFromListEarly = (processedData.oClaseBAPI.listTRETURN || []).filter(
      (item) => item.TYPE === "E" && item.MESSAGE?.trim(),
    );
    const sapRespondioError = errorsFromListEarly.length > 0 || postData.returnValue !== 0;
    if (sapRespondioError && formaPreservada) {
      ensureListCabFormaFirstRow(processedData.oClaseBAPI);
      const cabOnErr = processedData.oClaseBAPI?.listCABFRM?.clsZES_CAB_FORMA?.[0];
      overlayPreservedCabFormaIfSapError(cabOnErr, formaPreservada);
    }

    const cabinResp =
      processedData.oClaseBAPI?.listCABIN?.clsZES_CAB_INPUT?.[0] ||
      (Array.isArray(processedData.oClaseBAPI?.listCABIN)
        ? processedData.oClaseBAPI.listCABIN[0]
        : null);
    if (cabinResp) {
      if ((!cabinResp.MONTAJE || cabinResp.MONTAJE === "") && montajePreservado === "X")
        cabinResp.MONTAJE = montajePreservado;
      if ((!cabinResp.BISEL || cabinResp.BISEL === "") && biselCabinPreservado === "X")
        cabinResp.BISEL = biselCabinPreservado;
    }

    erroresPedido.value = [];
    warningsPedido.value = [];

    if (processedData.oClaseBAPI.listTRETURN?.length > 0) {
      const errorsFromList = processedData.oClaseBAPI.listTRETURN.filter(
        (item) => item.TYPE === "E" && item.MESSAGE?.trim(),
      );
      const warningsFromList = processedData.oClaseBAPI.listTRETURN.filter(
        (item) => (item.TYPE === "W" || item.TYPE === "I") && item.MESSAGE?.trim(),
      );
      let allErrors = [];
      if (postData.returnValue !== 0 && postData.MensajeErrorSAP?.trim())
        allErrors.push({ MESSAGE: postData.MensajeErrorSAP, TYPE: "E" });
      if (errorsFromList.length > 0) allErrors = allErrors.concat(errorsFromList);
      if (allErrors.length > 0) setErroresPedidoRaw(allErrors);
      if (warningsFromList.length > 0) setWarningsPedidoRaw(warningsFromList);
    } else {
      if (postData.returnValue !== 0 && postData.MensajeErrorSAP?.trim())
        setErroresPedidoRaw([{ MESSAGE: postData.MensajeErrorSAP, TYPE: "E" }]);
    }

    // Alinear ORIGEN de listFORMAS con listCABFRM.ORIGEN_DATOS (p. ej. SAP devuelve "B" en clsZFORMAS con IndoScan "F")
    {
      const b = processedData.oClaseBAPI;
      const od = b?.listCABFRM?.clsZES_CAB_FORMA?.[0]?.ORIGEN_DATOS;
      const str = od != null ? String(od).trim() : "";
      const rows = b?.listFORMAS?.clsZFORMAS;
      if (str && str !== "0" && Array.isArray(rows)) {
        for (const f of rows) {
          if (f && typeof f === "object") f.ORIGEN = str;
        }
      }
    }

    pedidoRef.value = processedData;

    const erroresGuardados = erroresPedido.value || [];
    if (erroresGuardados.length > 0)
      throw new Error(erroresGuardados[0] || "Se encontraron errores en el pedido");

    return pedidoRef.value;
  }

  function NormalizarPedido({ pedido }) {
    return processAfterReceivePedido(pedido);
  }

  async function RealizarPedido() {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();

    const cabFormaAntes = pedidoRef.value?.oClaseBAPI?.listCABFRM?.clsZES_CAB_FORMA?.[0];
    const formaPreservadaEnvio = snapshotCabForma(cabFormaAntes);

    const bapi = pedidoRef.value?.oClaseBAPI;
    const cabinAntesEnvio =
      bapi?.listCABIN?.clsZES_CAB_INPUT?.[0] ||
      (Array.isArray(bapi?.listCABIN) ? bapi.listCABIN[0] : null);
    const montajePreservadoEnvio = cabinAntesEnvio?.MONTAJE || "";
    const biselCabinPreservadoEnvio = cabinAntesEnvio?.BISEL || "";

    const clonar = (v) => (v ? JSON.parse(JSON.stringify(v)) : null);
    const datosCalculadosPreservados = {
      ojoOutDerecho: clonar(bapi?.ojoOutDerecho),
      ojoOutIzquierdo: clonar(bapi?.ojoOutIzquierdo),
      datosRetornoDerecho: clonar(bapi?.datosRetornoDerecho),
      datosRetornoIzquierdo: clonar(bapi?.datosRetornoIzquierdo),
      FormaROD: bapi?.FormaROD || null,
      FormaROI: bapi?.FormaROI || null,
      FormaSOD: bapi?.FormaSOD || null,
      FormaSOI: bapi?.FormaSOI || null,
      FormaEOD: bapi?.FormaEOD || null,
      FormaEOI: bapi?.FormaEOI || null,
      FormaMOD: bapi?.FormaMOD || null,
      FormaMOI: bapi?.FormaMOI || null,
      FormaNOD: bapi?.FormaNOD || null,
      FormaNOI: bapi?.FormaNOI || null,
    };

    const pedidoCopia = JSON.parse(JSON.stringify(pedidoRef.value));
    const xml = json2xml(processBeforeSendPedido(pedidoCopia).oClaseBAPI);

    const idioma = getSoapIdioma();

    const data = await requests.realizarPedido(auth.token, xml, idioma);
    let postData = response.realizarPedido(data);
    const processedData = processAfterReceivePedido(postData);

    if (formaPreservadaEnvio) {
      const cabFormaResp = processedData.oClaseBAPI?.listCABFRM?.clsZES_CAB_FORMA?.[0];
      if (cabFormaResp) restoreCamposForma(cabFormaResp, formaPreservadaEnvio);
    }

    const cabinRespEnvio =
      processedData.oClaseBAPI?.listCABIN?.clsZES_CAB_INPUT?.[0] ||
      (Array.isArray(processedData.oClaseBAPI?.listCABIN)
        ? processedData.oClaseBAPI.listCABIN[0]
        : null);
    if (cabinRespEnvio) {
      if (
        (!cabinRespEnvio.MONTAJE || cabinRespEnvio.MONTAJE === "") &&
        montajePreservadoEnvio === "X"
      )
        cabinRespEnvio.MONTAJE = montajePreservadoEnvio;
      if (
        (!cabinRespEnvio.BISEL || cabinRespEnvio.BISEL === "") &&
        biselCabinPreservadoEnvio === "X"
      )
        cabinRespEnvio.BISEL = biselCabinPreservadoEnvio;
    }

    erroresPedido.value = [];
    warningsPedido.value = [];

    if (processedData.oClaseBAPI.listTRETURN?.length > 0) {
      const errorsFromList = processedData.oClaseBAPI.listTRETURN.filter(
        (item) => item.TYPE === "E" && item.MESSAGE?.trim(),
      );
      const warning562 = processedData.oClaseBAPI.listTRETURN.find(
        (item) => (item.TYPE === "W" || item.TYPE === "I") && item.NUMBER === "562",
      );
      if (warning562) {
        const fecha = (warning562.MESSAGE_V3 || "").split(";")[0];
        const numeroPedido = warning562.MESSAGE_V2 || "";
        processedData.oClaseBAPI.duplicado = { fecha, numeroPedido };
      }
      const warningsFromList = processedData.oClaseBAPI.listTRETURN.filter(
        (item) =>
          (item.TYPE === "W" || item.TYPE === "I") && item.MESSAGE?.trim() && item.NUMBER !== "562",
      );
      let allErrors = [];
      if (postData.returnValue !== 0 && postData.MensajeErrorSAP?.trim())
        allErrors.push({ MESSAGE: postData.MensajeErrorSAP, TYPE: "E" });
      if (errorsFromList.length > 0) allErrors = allErrors.concat(errorsFromList);
      if (allErrors.length > 0) setErroresPedidoRaw(allErrors);
      if (warningsFromList.length > 0) setWarningsPedidoRaw(warningsFromList);
    } else {
      if (postData.returnValue !== 0 && postData.MensajeErrorSAP?.trim())
        setErroresPedidoRaw([{ MESSAGE: postData.MensajeErrorSAP, TYPE: "E" }]);
    }

    const erroresGuardados = erroresPedido.value || [];
    if (erroresGuardados.length > 0)
      throw new Error(erroresGuardados[0] || "Se encontraron errores en el pedido");

    pedidoRef.value = processedData;

    if (pedidoRef.value?.oClaseBAPI) {
      const campos = [
        "ojoOutDerecho",
        "ojoOutIzquierdo",
        "datosRetornoDerecho",
        "datosRetornoIzquierdo",
        "FormaROD",
        "FormaROI",
        "FormaSOD",
        "FormaSOI",
        "FormaEOD",
        "FormaEOI",
        "FormaMOD",
        "FormaMOI",
        "FormaNOD",
        "FormaNOI",
      ];
      campos.forEach((campo) => {
        if (datosCalculadosPreservados[campo])
          pedidoRef.value.oClaseBAPI[campo] = datosCalculadosPreservados[campo];
      });
    }

    return pedidoRef.value;
  }

  function GetErroresPedido() {
    return erroresPedido.value;
  }

  function GetWarningsPedido() {
    return warningsPedido.value;
  }

  function GuardarPedido({ pedido }) {
    pedidoRef.value = { oClaseBAPI: { ...pedido } };
  }

  async function GuardarFavoritos({ favoritos }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    if (!profile.value) await PostLogin();
    const fav = favoritos.map((f) => {
      delete f.ZFLAG_MB;
      delete f.DESC_EDI_NET;
      f.KUNNR = profile.value.AreaVenta.CLIENTESAP;
      return f;
    });
    const data = await requests.saveFavoritos(
      auth.token,
      profile.value,
      json2xml({ clsFavoritos: fav }),
    );
    const postData = response.saveFavoritos(data);
    if (postData.returnValue !== 0) throw new Error(postData.MensajeErrorSAP);
  }

  async function GuardarListaFavoritos({ favoritos }) {
    if (!favoritos || !Array.isArray(favoritos))
      throw new Error("La lista de favoritos no es válida");
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    if (!profile.value) await PostLogin();
    const fav = favoritos.map((f) => {
      const favCopy = { ...f };
      delete favCopy.ZFLAG_MB;
      delete favCopy.DESC_EDI_NET;
      favCopy.KUNNR = profile.value.AreaVenta.CLIENTESAP;
      return favCopy;
    });
    const data = await requests.saveFavoritos(
      auth.token,
      profile.value,
      json2xml({ clsFavoritos: fav }),
    );
    const postData = response.saveFavoritos(data);
    if (postData.returnValue !== 0) throw new Error(postData.MensajeErrorSAP);
  }

  function normalizeRecetasDateRange(dateRange = {}) {
    let fecha_low = dateRange.fecha_low;
    let fecha_hi = dateRange.fecha_hi;
    if (!fecha_low || !fecha_hi) {
      const today = new Date();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      fecha_low = fecha_low || sixMonthsAgo.toLocaleDateString("es-ES");
      fecha_hi = fecha_hi || today.toLocaleDateString("es-ES");
    }
    return { fecha_low, fecha_hi };
  }

  async function GetRecetas(dateRange = {}) {
    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      if (!auth.username || !auth.token) await PostLogin();
      if (!profile.value) await PostLogin();

      const listaAreasCliente = profile.value?.AreaVenta?.listaAreasCliente || [];
      if (listaAreasCliente.length === 0) {
        recetas.value = { oClaseBAPI: { listRECETAS: [] } };
        return false;
      }

      const allRecetas = [];
      let firstPostData = null;
      let hasError = false;

      for (const areaCliente of listaAreasCliente) {
        try {
          const areaProfile = JSON.parse(JSON.stringify(profile.value));
          if (areaProfile.AreaVenta) {
            areaProfile.AreaVenta.ORG_VENTA = areaCliente.ORG_VENTA;
            areaProfile.AreaVenta.CANAL_DIST = areaCliente.CANAL_DIST;
            areaProfile.AreaVenta.SECTOR = areaCliente.SECTOR;
          }

          const areaData = await requests.getRecetas(
            auth.username,
            auth.token,
            areaProfile,
            dateRange,
          );
          if (areaData) {
            const areaPostData = response.recetas(areaData);
            if (!firstPostData) firstPostData = areaPostData;
            if (areaPostData.oClaseBAPI?.listRECETAS) {
              allRecetas.push(
                ...areaPostData.oClaseBAPI.listRECETAS.map((r) => ({
                  ...r,
                  VKORG: areaCliente.ORG_VENTA,
                })),
              );
            }
            if (areaPostData.returnValue !== 0) hasError = true;
          }
        } catch (error) {
          // Token caducado: el interceptor ya está cerrando la sesión; no
          // tiene sentido seguir consultando el resto de áreas.
          if (isSessionExpiredError(error)) throw error;
          hasError = true;
        }
      }

      const combinedData = {
        ...(firstPostData || {}),
        oClaseBAPI: { ...(firstPostData?.oClaseBAPI || {}), listRECETAS: allRecetas },
      };
      if (!firstPostData) {
        recetas.value = { oClaseBAPI: { listRECETAS: [] } };
        return false;
      }
      if (hasError && allRecetas.length === 0) {
        recetas.value = {
          oClaseBAPI: { listRECETAS: [] },
          MensajeErrorSAP: firstPostData.MensajeErrorSAP,
        };
        throw new Error(firstPostData.MensajeErrorSAP || "Error obteniendo las recetas");
      }
      recetas.value = combinedData;
      mensajeRecetas.value = combinedData.MensajeErrorSAP || null;
      lastRecetasFetchRange.value = normalizeRecetasDateRange(dateRange);
      return true;
    } catch (error) {
      if (isSessionExpiredError(error)) throw error;
      recetas.value = { oClaseBAPI: { listRECETAS: [] }, MensajeErrorSAP: error.message };
      mensajeRecetas.value = error.message;
      throw error;
    }
  }

  async function GuardarReceta({ recetaData }) {
    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      const processedPedido = { oClaseBAPI: { ...recetaData } };
      const processed = processBeforeSaveReceta(processedPedido);
      const data = await requests.guardarReceta(
        auth.username,
        auth.token,
        profile.value,
        processed.oClaseBAPI,
      );
      if (data.returnValue !== 0 || data.MensajeErrorSAP?.trim())
        throw new Error(data.MensajeErrorSAP || "Error al guardar la receta");
      return data;
    } catch (error) {
      throw error;
    }
  }

  async function RecuperarReceta({ receta, cliente }) {
    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      if (!auth.username || !auth.token) await PostLogin();
      if (!profile.value) await PostLogin();

      const data = await requests.recuperarReceta(
        auth.username,
        auth.token,
        profile.value,
        receta,
        cliente,
      );
      const postData = response.recuperarReceta(data);

      switch (postData.returnValue) {
        case 0: {
          const processedData = processAfterReceivePedido(postData);
          pedidoRef.value = processedData;

          const listOJOIN = processedData.oClaseBAPI?.listOJOIN?.clsZES_OJO_INPUT || [];
          const ojoOD = Array.isArray(listOJOIN) ? listOJOIN.find((o) => o.ZOJO === "D") : null;
          const ojoOI = Array.isArray(listOJOIN) ? listOJOIN.find((o) => o.ZOJO === "I") : null;

          const mapGraduation = (ojo) => {
            if (!ojo) return null;
            const cilindro = parseSignedValue(ojo.SIGNO_CIL, ojo.CILINDRO);
            return {
              esfera: parseSignedValue(ojo.SIGNO_ESF, ojo.ESFERA),
              // Un cilindro 0 (recetas Rodenstock sin astigmatismo) debe quedar
              // vacío en el form: si se aplica el 0 literal, aguas abajo peta.
              cilindro: cilindro === 0 ? "" : cilindro,
              eje: ojo.EJE ? parseFloat(ojo.EJE) || "" : "",
              adicion: ojo.ADICION ? parseFloat(ojo.ADICION) : null,
              stock: ojo.SOLO_STOCK === "X" || ojo.SOLO_STOCK === true,
              informativo: ojo.SOLO_INFOR === "X" || ojo.SOLO_INFOR === true,
              isFavorito: false,
            };
          };

          const graduationData = {
            od: mapGraduation(ojoOD),
            oi: mapGraduation(ojoOI),
            fromSavedRecipe: true,
          };

          const cabin =
            processedData.oClaseBAPI?.listCABIN?.clsZES_CAB_INPUT?.[0] ||
            (Array.isArray(processedData.oClaseBAPI?.listCABIN)
              ? processedData.oClaseBAPI.listCABIN[0]
              : null);
          // Mismo chain de fallback que usa el GetCaracteristicasLente interno
          // de RecuperarReceta más abajo (~línea 1499-1502): SAP no siempre
          // popula CABOUT.VKORG, así que sin el fallback a CABIN.VKORG la
          // receta se guardaba sin vkorg, syncAreaClienteWithVkorg salía por
          // la rama de "!vkorg" y todas las llamadas posteriores
          // (GetCaracteristicasLente, GetDiametros, etc.) viajaban con el
          // vkorg del usuario actual.
          const vkorg =
            processedData.oClaseBAPI?.CABOUT?.VKORG ||
            processedData.oClaseBAPI?.VKORG ||
            cabin?.VKORG ||
            null;
          if (vkorg) graduationData.vkorg = vkorg;
          const referencia =
            cabin?.REF_CLIENT ||
            processedData.oClaseBAPI?.listCABOUT?.clsZES_CAB_OUTPUT?.[0]?.REF_CLIENTE ||
            processedData.oClaseBAPI?.CABOUT?.REF_CLIENTE ||
            null;
          graduationData.referencia = referencia || null;
          graduationData.montaje = cabin?.MONTAJE === "X" ? "X" : null;
          graduationData.codigoPromocional = cabin?.CODI_PROM || "";
          // Marker REFR. SUBJETIVA: el flujo CNXT escribe `DATOS IMP. CNXT`
          // en CABIN.OBSERV (ver GetDatosCNXT_REFR_SUBJETIVA.js). Lo
          // exponemos para que `BasicData` pueda bloquear graduación y
          // prismas al recuperar la receta.
          graduationData.observ = cabin?.OBSERV || null;

          const lensData = ojoOD || ojoOI;
          if (lensData) {
            let fotocromatico = lensData.FOTOCROMICO || lensData.FOTOCROMATICO || null;
            let tratamiento = lensData.TRATAM1 || null;
            const jerga = lensData.JER_EDI_INET;
            let focomaterial = lensData.FOCO_MATERIAL || null;
            let indice = lensData.IND_REF || null;

            if (jerga && (!fotocromatico || !tratamiento || !focomaterial || !indice)) {
              try {
                const { useOfertasStore } = await import("./ofertas");
                const ofertasStore = useOfertasStore();
                const vkorgVal =
                  vkorg ||
                  cabin?.VKORG ||
                  processedData.oClaseBAPI?.listCABIN?.clsZES_CAB_INPUT?.[0]?.VKORG;
                const kunnrVal = cabin?.KUNNR || cliente;
                const caracteristicas = await ofertasStore.GetCaracteristicasLente({
                  payload: { Vkorg: vkorgVal, Cliente: kunnrVal, Lente: jerga },
                });
                if (caracteristicas) {
                  const c = caracteristicas.data || caracteristicas;
                  const pick = (obj, ...keys) => {
                    for (const k of keys) {
                      if (obj && k in obj) return obj[k];
                    }
                    return null;
                  };
                  fotocromatico =
                    fotocromatico || pick(c, "Fotocromico", "Fotocromatico", "FOTOCROMICO") || null;
                  tratamiento = tratamiento || pick(c, "Tratamiento", "TRATAM1", "Tratami") || null;
                  focomaterial = focomaterial || pick(c, "FocoMaterial", "FOCO_MATERIAL") || null;
                  indice = indice || pick(c, "Indice", "IND_REF") || null;
                }
              } catch (e) {
              }
            }

            graduationData.lens = {
              lente: jerga || null,
              diametro: lensData.DIAMETRO || null,
              fotocromatico,
              focomaterial,
              indice,
              coloracion: lensData.COLORACION || null,
              color: lensData.COLOR || null,
              tono: lensData.TONO || null,
              tratamiento,
            };
          }

          // Additional data, shape data, suplementos - abbreviated but complete
          if (ojoOD || ojoOI) {
            graduationData.additionalData = {
              altAroOD: ojoOD?.ALT_ARO || null,
              altAroOI: ojoOI?.ALT_ARO || null,
              distNpOD: ojoOD?.DIST_NP || null,
              distNpOI: ojoOI?.DIST_NP || null,
              distVertOD: ojoOD?.DIST_VERT || null,
              distVertOI: ojoOI?.DIST_VERT || null,
              gradPantos: (ojoOD || ojoOI)?.GRAD_PANTOS || null,
              gradEnvolv: (ojoOD || ojoOI)?.GRAD_ENVOLV || null,
              distTrab: (ojoOD || ojoOI)?.DIST_TRAB || null,
              garantiaNom: (ojoOD || ojoOI)?.GARANTIA_NOM || null,
              garantia1Ap: (ojoOD || ojoOI)?.GARANTIA_1AP || null,
              garantia2Ap: (ojoOD || ojoOI)?.GARANTIA_2AP || null,
              vmap: (ojoOD || ojoOI)?.VMAP || null,
              iniciales: (ojoOD || ojoOI)?.INICIALES || null,
              // IDL_BIGEXACT vive en `ojoOD/ojoOI.IDL_BIGEXACT` en BAPI;
              // lo aplanamos para que `applySavedAdditionalData` lo pueda
              // leer aunque el caller no nos pase `eyeData`. Sin esto se
              // perdía al recuperar la receta. Mapeo paralelo al de
              // `mapPedidoToOrderForm` (helpers/orderDuplication.js).
              idlBigExact:
                (ojoOD || ojoOI)?.IDL_BIGEXACT ||
                (ojoOD || ojoOI)?.idlBigExact ||
                (ojoOD || ojoOI)?.idl_bigexact ||
                null,
              altPas: (ojoOD || ojoOI)?.ALT_PAS || null,
              inset0OD: ojoOD?.INSET0 || null,
              inset0OI: ojoOI?.INSET0 || null,
              isotipo: ojoOD?.ISOTIPO || ojoOI?.ISOTIPO || null,
              isotipoRod: ojoOD?.ISOTIPO_ROD || ojoOI?.ISOTIPO_ROD || null,
              tipoIsotipo: ojoOD?.TIPO_ISOTIPO || ojoOI?.TIPO_ISOTIPO || null,
              claseProg: (ojoOD || ojoOI)?.CLASE_PROG || null,
              tipoProg: (ojoOD || ojoOI)?.TIPO_PROG || null,
              visualPref: (ojoOD || ojoOI)?.VISUAL_PREF || null,
            };
          }

          const cabfrm = processedData.oClaseBAPI?.listCABFRM?.clsZES_CAB_FORMA?.[0];
          if (cabfrm) {
            graduationData.shapeData = {
              origenDatos: cabfrm.ORIGEN_DATOS || null,
              formaBasica: cabfrm.FORMA_BASICA || null,
              calibre: cabfrm.CALIBRE || null,
              puente: cabfrm.PUENTE || null,
              altBox: cabfrm.ALT_BOX || null,
              marca: cabfrm.MARCA || null,
              modelo: cabfrm.MODELO || null,
              tipoMontura: cabfrm.TIPO_MONTURA || null,
              bisel: cabfrm.BISEL || null,
              orientacion: cabfrm.ORIENTACION || null,
              precalibrado: ojoOD?.PRECALIBRADO || ojoOI?.PRECALIBRADO || null,
              espBordeOD: ojoOD?.ESP_BORDE ?? null,
              espBordeOI: ojoOI?.ESP_BORDE ?? null,
              redimension: {
                TEM_SIGN: cabfrm.TEM_SIGN,
                TEM_COTA: cabfrm.TEM_COTA,
                SUP_SIGN: cabfrm.SUP_SIGN,
                SUP_COTA: cabfrm.SUP_COTA,
                INF_SIGN: cabfrm.INF_SIGN,
                INF_COTA: cabfrm.INF_COTA,
                TOT_SIGN: cabfrm.TOT_SIGN,
                TOT_COTA: cabfrm.TOT_COTA,
              },
            };
          }

          const rawFormas = processedData.oClaseBAPI?.listFORMAS;
          const formasNorm =
            Array.isArray(rawFormas) && rawFormas.length > 0
              ? { clsZFORMAS: [...rawFormas] }
              : rawFormas?.clsZFORMAS?.length > 0
                ? rawFormas
                : null;
          if (formasNorm) graduationData.listFORMAS = formasNorm;

          graduationData.eyeData = { ojoOD, ojoOI };

          if (graduationData.od || graduationData.oi)
            localStorage.setItem("savedRecipeData", JSON.stringify(graduationData));

          return postData;
        }
        default:
          throw new Error(postData.MensajeErrorSAP || "Error al recuperar la receta");
      }
    } catch (error) {
      throw error;
    }
  }

  async function BorrarRecetas({ recetas: recetasList }) {
    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      if (!auth.username || !auth.token) await PostLogin();
      if (!profile.value) await PostLogin();

      const recetasData = {
        CLIENTE: profile.value.AreaVenta.CLIENTE,
        ORG_VENTA: profile.value.AreaVenta.ORG_VENTA,
        CANAL_DIST: profile.value.AreaVenta.CANAL_DIST,
        SECTOR: profile.value.AreaVenta.SECTOR,
        listRECETAS: {
          clsZSS_RCTA_HD: recetasList.map((receta) => ({
            SELECCION: true,
            MANDT: receta.MANDT || "",
            KUNNR: receta.KUNNR || profile.value.AreaVenta.CLIENTE,
            RECETA: receta.RECETA,
            ZFECHA_GRA: receta.ZFECHA_GRA,
            ZHORA_GRA: receta.ZHORA_GRA,
            REF_CLIENT: receta.REF_CLIENT || "",
            KUNWE: receta.KUNWE || receta.KUNNR || profile.value.AreaVenta.CLIENTE,
            OBSERV: receta.OBSERV || receta.PROD || "",
            ESTADO: receta.ESTADO || "C",
            PROD: receta.PROD || "",
            NAME: receta.NAME || "",
            STREET: receta.STREET || "",
            POSTL_CODE: receta.POSTL_CODE || "",
            CITY: receta.CITY || "",
            COUNTRY: receta.COUNTRY || "",
            TELEPHONE: receta.TELEPHONE || "",
            FLAG_MONT: receta.FLAG_MONT || "",
            ENVIO_USUARIO: receta.ENVIO_USUARIO || "",
            Texto_tabla: receta.Texto_tabla || "",
            VKORG: receta.VKORG || profile.value.AreaVenta.ORG_VENTA,
            VTWEG: receta.VTWEG || profile.value.AreaVenta.CANAL_DIST,
          })),
        },
      };

      const data = await requests.borrarRecetas(auth.token, profile.value, recetasData);
      const postData = response.borrarRecetas(data);
      if (postData.returnValue !== 0)
        throw new Error(postData.MensajeErrorSAP || "Error al borrar las recetas");
      return postData;
    } catch (error) {
      throw error;
    }
  }

  async function EnviarRecetas({ recetas: recetasList, destinatario, observaciones }) {
    if (!recetasList || recetasList.length === 0) return;
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    const prof = profile.value;
    const recetasData = {
      CLIENTE: prof.AreaVenta.CLIENTE,
      ORG_VENTA: prof.AreaVenta.ORG_VENTA,
      CANAL_DIST: prof.AreaVenta.CANAL_DIST,
      SECTOR: prof.AreaVenta.SECTOR,
      listRECETAS: {
        clsZSS_RCTA_HD: recetasList.map((r) => ({
          SELECCION: true,
          MANDT: r.MANDT || "",
          KUNNR: r.KUNNR || prof.AreaVenta.CLIENTE,
          RECETA: r.RECETA,
          ZFECHA_GRA: r.ZFECHA_GRA,
          ZHORA_GRA: r.ZHORA_GRA,
          REF_CLIENT: r.REF_CLIENT || "",
          KUNWE: destinatario || r.KUNWE || r.KUNNR || prof.AreaVenta.CLIENTE,
          OBSERV: observaciones || r.OBSERV || r.PROD || "",
          ESTADO: r.ESTADO || "C",
          PROD: r.PROD || "",
          NAME: r.NAME || "",
          STREET: r.STREET || "",
          POSTL_CODE: r.POSTL_CODE || "",
          CITY: r.CITY || "",
          COUNTRY: r.COUNTRY || "",
          TELEPHONE: r.TELEPHONE || "",
          FLAG_MONT: r.FLAG_MONT || "",
          ENVIO_USUARIO: r.ENVIO_USUARIO || "",
          Texto_tabla: r.Texto_tabla || "",
          VKORG: r.VKORG || prof.AreaVenta.ORG_VENTA,
          VTWEG: r.VTWEG || prof.AreaVenta.CANAL_DIST,
        })),
      },
    };
    const data = await requests.enviarRecetas(auth.token, prof, recetasData);
    const postData = response.enviarRecetas(data);
    if (postData.returnValue !== 0)
      throw new Error(postData.MensajeErrorSAP || "Error al enviar las recetas");
    return postData;
  }

  async function BorrarPedido({ cliente, refPedido, year, vkorg, vtweg, spart, motivoAnulacion }) {
    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      if (!auth.token || !profile.value) await PostLogin();
      const customerNo = cliente || profile.value?.DatosConfig?.CUSTOMERNO;
      if (!customerNo) throw new Error("No se pudo obtener el número de cliente");
      const org = vkorg || profile.value?.AreaVenta?.ORG_VENTA;
      const canal = vtweg || profile.value?.AreaVenta?.CANAL_DIST;
      const sector = spart || profile.value?.AreaVenta?.SECTOR;
      const data = await requests.borrarPedido(
        auth.token,
        customerNo,
        refPedido,
        year,
        org,
        canal,
        sector,
        motivoAnulacion,
      );
      const postData = response.borrarPedido(data);
      if (postData.returnValue !== 0)
        throw new Error(postData.MensajeErrorSAP || "Error al borrar el pedido");
      return postData;
    } catch (error) {
      throw error;
    }
  }

  async function GetComercialFotos(options = {}) {
    const forceRefresh = options.forceRefresh === true;
    const maxAgeMs = typeof options.maxAgeMs === "number" ? options.maxAgeMs : 15 * 60 * 1000;

    if (
      !forceRefresh &&
      comercialFotosFetchedAt.value > 0 &&
      Date.now() - comercialFotosFetchedAt.value < maxAgeMs
    ) {
      return comercialPhotos.value;
    }

    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      if (!auth.token) await PostLogin();
      if (!comerciales.value?.length) await PostLogin();
      if (!comerciales.value?.length) {
        comercialFotosFetchedAt.value = Date.now();
        return {};
      }

      const photos = {};
      for (const comercial of comerciales.value) {
        if (comercial.CUENTA_CORREO) {
          try {
            const apiResponse = await requests.getComercialFoto(
              comercial.CUENTA_CORREO,
              auth.token,
            );
            const postData = response.getComercialFoto(apiResponse);
            if (postData?.Image)
              photos[comercial.CUENTA_CORREO] = `data:image/jpeg;base64,${postData.Image}`;
          } catch (error) {
          }
        }
      }
      comercialPhotos.value = photos;
      comercialFotosFetchedAt.value = Date.now();
      return photos;
    } catch (error) {
      throw error;
    }
  }

  async function GetAlertasCliente() {
    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      if (!auth.token) await PostLogin();
      if (!profile.value) await PostLogin();

      const cliente = profile.value?.AreaVenta?.CLIENTE || profile.value?.DatosConfig?.CUSTOMERNO;
      if (!cliente) {
        alertas.value = [];
        return [];
      }

      const axiosResponse = await requests.getAlertasCliente(cliente, auth.token);
      const xmlString = axiosResponse.data;
      let alertasList = [];

      const jsonMatch = xmlString.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const jsonData = JSON.parse(jsonMatch[0]);
          if (jsonData?.Alertas && Array.isArray(jsonData.Alertas)) alertasList = jsonData.Alertas;
        } catch (parseError) {
        }
      }

      const mappedAlertas = alertasList.map((alert) => {
        if (typeof alert === "string") {
          let status = "warning",
            tag = "Alerta";
          if (alert.includes("Pendiente de Arreglo")) {
            tag = "Devolución de lentes";
            status = "warning";
          } else if (alert.includes("Fecha Prev")) {
            tag = "Entrega de lentes";
            status = "ok";
          }
          return { status, tag, text: alert, date: "", unread: true, original: alert };
        }
        return {
          status: alert.tipo || alert.status || "warning",
          tag: alert.categoria || alert.tag || alert.tipo || "Alerta",
          text: alert.mensaje || alert.texto || alert.descripcion || alert.text || "",
          date: alert.fecha || alert.date || alert.timestamp || "",
          unread: alert.leida === false || alert.unread === true || alert.leido === false,
          original: alert,
        };
      });

      alertas.value = mappedAlertas;
      return mappedAlertas;
    } catch (error) {
      alertas.value = [];
      throw error;
    }
  }

  function setDetallesObject(data) {
    detallesObject.value = data;
  }
  function addWarningPedido(warning) {
    if (warning) warningsPedido.value.push(warning);
  }
  function emptyWarningsPedido() {
    warningsPedido.value = [];
  }
  function emptyErroresPedido() {
    erroresPedido.value = [];
  }
  function setErroresPedido(errores) {
    erroresPedido.value = Array.isArray(errores) ? errores : [];
  }
  function removeErrorPedido(index) {
    if (index >= 0 && index < erroresPedido.value.length) {
      erroresPedido.value = erroresPedido.value.filter((_, i) => i !== index);
    }
  }
  function setPedido(data) {
    pedidoRef.value = processAfterReceivePedido(data);
  }
  function setWarningsPedido(data) {
    setWarningsPedidoRaw(data);
  }

  async function reportarErrorPedido({ nombre, email, mensaje }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();
    if (!auth?.token) await PostLogin();
    if (!auth?.token) throw new Error("No session");

    const escapeXml = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const formatErr = (e) => {
      if (typeof e === "string") return e.replace(/^Error:\s*/i, "").trim();
      if (e?.MESSAGE) {
        return [e.MESSAGE, e.MESSAGE_V1, e.MESSAGE_V2, e.MESSAGE_V3, e.MESSAGE_V4]
          .filter(Boolean)
          .join(" ");
      }
      return "";
    };
    /** CDATA permite HTML en <Errores>; la secuencia ]]> se parte para ser válida en CDATA */
    const cdataSafe = (s) => String(s ?? "").replace(/]]>/g, "]]]]><![CDATA[>");
    const erroresText = cdataSafe(
      (erroresPedido.value || []).map(formatErr).filter(Boolean).join("\n"),
    );

    const pedido = JSON.parse(JSON.stringify(pedidoRef.value || {}));
    if (!pedido.oClaseBAPI) pedido.oClaseBAPI = {};
    let xmlBapi;
    try {
      xmlBapi = json2xml(processBeforeSendPedido(pedido).oClaseBAPI);
    } catch {
      xmlBapi = json2xml(pedido.oClaseBAPI);
    }

    const idioma = getSoapIdioma();

    const data = await requests.reportarErrorPedido(auth.token, {
      nombreUsuario: escapeXml(nombre),
      emailUsuario: escapeXml(email),
      mensajeUsuario: escapeXml(mensaje),
      errores: erroresText,
      oClaseApiXml: xmlBapi,
      idioma,
    });
    return response.reportarErrorPedido(data);
  }

  return {
    // State
    profile,
    erroresPedido,
    warningsPedido,
    pedido: pedidoRef,
    comerciales,
    comercialPhotos,
    contactosATC,
    recetas,
    lastRecetasFetchRange,
    mensajeRecetas,
    detallesObject,
    alertas,
    importePendienteFacturar,
    preferredOrgVenta,
    pedidosData,
    pedidosFiltradosData,
    // Computed
    pedidos,
    pedidosFiltrados,
    homePedidos,
    getComerciales,
    brandOptions,
    suGestor,
    monedaCliente,
    simboloMoneda,
    recetasList,
    // Mutation-like
    setProfile,
    setPreferredOrgVenta,
    setDatosConfig,
    emptyData,
    setDetallesObject,
    addWarningPedido,
    emptyWarningsPedido,
    emptyErroresPedido,
    setErroresPedido,
    removeErrorPedido,
    setPedido,
    setWarningsPedido,
    // Actions
    PostLogin,
    GetContactosATC,
    GetPedidos,
    GetPedidoByNumero,
    GetNuevoPedido,
    SaveAjustes,
    RecuperarDatosOptica,
    SaveMailbox,
    ManagerBuscarFacturas,
    ManagerBuscarPendientesFacturar,
    ManagerGetBonos,
    ManagerBuscarListaClientes,
    ManagerGetFacturaSap,
    ManagerAbrirReclamacionFactura,
    ManagerRecuperarAlbaran,
    ManagerGetBonoDetalle,
    ManagerGetAllBonoDetalle,
    ManagerGetServicioEstadisticas,
    ManagerSendEmail347,
    ManagerSendEmailFacturas,
    GetBases,
    PrepararPedido,
    NormalizarPedido,
    RealizarPedido,
    GetErroresPedido,
    GetWarningsPedido,
    GuardarPedido,
    GuardarFavoritos,
    GuardarListaFavoritos,
    GetRecetas,
    GuardarReceta,
    RecuperarReceta,
    BorrarRecetas,
    EnviarRecetas,
    BorrarPedido,
    GetComercialFotos,
    GetAlertasCliente,
    reportarErrorPedido,
  };
}, {
  persist: {
    storage: localStorage,
    pick: ["profile", "comerciales", "preferredOrgVenta"],
  },
});
